import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/formatCurrency';
import { downloadReceiptPdf, printReceiptWindow } from '../../utils/receipt';

const kenyaToday = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Nairobi' });

const Stock = () => {
  const { user } = useAuth();
  const isFieldAgent = user?.role === 'agent' || user?.role === 'depot_sales';
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiringStock, setExpiringStock] = useState([]);
  const [expiringLoading, setExpiringLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [allDepotStock, setAllDepotStock] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    product: '',
    agent: '',
    startDate: ['agent', 'depot_sales'].includes(user?.role) ? kenyaToday() : '',
    endDate: ['agent', 'depot_sales'].includes(user?.role) ? kenyaToday() : ''
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(1000);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    totalItems: 0,
    totalValue: 0
  });
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [transferReceipt, setTransferReceipt] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [editData, setEditData] = useState({
    id: '',
    product: null,
    quantity: '',
    expiryDate: '',
    batchNumber: '',
    location: 'depot',
    agent: '',
    notes: ''
  });
  const [transferData, setTransferData] = useState({
    agent: '',
    notes: ''
  });
  const [transferItems, setTransferItems] = useState([
    { stockId: '', quantity: '', unitPrice: '' }
  ]);
  const [stockItems, setStockItems] = useState([
    { product: '', quantity: '', damagedQuantity: '', expiryDate: '', batchNumber: '' }
  ]);
  const [formData, setFormData] = useState({
    location: 'depot',
    agent: '',
    notes: ''
  });
  const [mergingDuplicates, setMergingDuplicates] = useState(false);

  useEffect(() => {
    fetchStock();
    fetchSummary();
  }, [page, limit, filters]);

  useEffect(() => {
    fetchExpiringStock();
    fetchProducts();
    if (user?.role === 'admin' || user?.role === 'finance') {
      fetchAgents();
    }
  }, []);

  const fetchStock = async () => {
    try {
      const response = await axios.get('/api/stock', {
        params: {
          page,
          limit,
          search: filters.search || undefined,
          location: filters.location || undefined,
          product: filters.product || undefined,
          agent: filters.agent || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          ...(isFieldAgent && !filters.startDate && !filters.endDate ? { all: true } : {})
        }
      });
      setStock(response.data.stock || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get('/api/stock/summary', {
        params: {
          search: filters.search || undefined,
          location: filters.location || undefined,
          product: filters.product || undefined,
          agent: filters.agent || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          ...(isFieldAgent && !filters.startDate && !filters.endDate ? { all: true } : {})
        }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching stock summary:', error);
    }
  };

  const fetchExpiringStock = async () => {
    try {
      const response = await axios.get('/api/stock/expiring', { params: { days: 7 } });
      setExpiringStock(response.data || []);
    } catch (error) {
      console.error('Error fetching expiring stock:', error);
    } finally {
      setExpiringLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products', {
        params: { limit: 1000 }
      });
      setProducts(response.data.products || []);
    } catch (fetchError) {
      console.error('Error fetching products:', fetchError);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/api/users/agents');
      setAgents(response.data || []);
    } catch (fetchError) {
      console.error('Error fetching agents:', fetchError);
    }
  };

  const fetchAllDepotStock = async () => {
    try {
      const response = await axios.get('/api/stock', {
        params: {
          location: 'depot',
          limit: 1000
        }
      });
      setAllDepotStock(response.data.stock || []);
    } catch (fetchError) {
      console.error('Error fetching depot stock:', fetchError);
    }
  };

  const openModal = async () => {
    setError('');
    setStockItems([{ product: '', quantity: '', damagedQuantity: '', expiryDate: '', batchNumber: '' }]);
    setFormData({ location: 'depot', agent: '', notes: '' });
    setShowModal(true);
    await Promise.all([fetchProducts(), fetchAgents()]);
  };

  const closeModal = () => {
    setShowModal(false);
    setStockItems([{ product: '', quantity: '', damagedQuantity: '', expiryDate: '', batchNumber: '' }]);
    setFormData({ location: 'depot', agent: '', notes: '' });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStockItemChange = (index, field, value) => {
    setStockItems((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
    );
  };

  const addStockItem = () => {
    setStockItems((prev) => [...prev, { product: '', quantity: '', damagedQuantity: '', expiryDate: '', batchNumber: '' }]);
  };

  const removeStockItem = (index) => {
    setStockItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        items: stockItems.map(item => ({
          product: item.product,
          quantity: Number(item.quantity),
          damagedQuantity: item.damagedQuantity === '' ? 0 : Number(item.damagedQuantity),
          expiryDate: item.expiryDate,
          batchNumber: item.batchNumber || undefined
        })),
        location: formData.location,
        agent: formData.location === 'agent' ? formData.agent : undefined,
        notes: formData.notes
      };

      const response = await axios.post('/api/stock', payload, {
        timeout: 30000
      });

      if (response.status === 200 || response.status === 201) {
        setStockItems([{ product: '', quantity: '', damagedQuantity: '', expiryDate: '', batchNumber: '' }]);
        setFormData({ location: 'depot', agent: '', notes: '' });
        setShowModal(false);
        fetchStock();
        fetchSummary();
      }
    } catch (submitError) {
      console.error('Error adding stock:', submitError);
      if (submitError.code === 'ECONNABORTED') {
        setError('Request timeout. Please check if the stock was added before trying again.');
      } else {
        setError(
          submitError.response?.data?.message || 'Failed to add stock. Please check your connection and try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openTransferModal = async (item) => {
    setTransferError('');
    setTransferReceipt(null);
    setTransferData({
      agent: '',
      notes: ''
    });
    setTransferItems([
      { stockId: item?._id || '', quantity: '', unitPrice: item?.product?.unitPrice ?? '' }
    ]);
    setTransferModalOpen(true);
    await Promise.all([fetchAgents(), fetchAllDepotStock()]);
  };

  const closeTransferModal = () => {
    setTransferModalOpen(false);
    setTransferReceipt(null);
  };

  const openEditModal = async (item) => {
    setEditError('');
    setEditData({
      id: item._id,
      product: item.product || null,
      quantity: item.quantity || '',
      expiryDate: item.expiryDate ? item.expiryDate.slice(0, 10) : '',
      batchNumber: item.batchNumber || '',
      location: item.location || 'depot',
      agent: item.agent?._id || '',
      notes: item.notes || ''
    });
    setEditModalOpen(true);
    if (agents.length === 0) {
      await fetchAgents();
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditData({
      id: '',
      product: null,
      quantity: '',
      expiryDate: '',
      batchNumber: '',
      location: 'depot',
      agent: '',
      notes: ''
    });
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    setEditSubmitting(true);
    setEditError('');

    try {
      const payload = {
        quantity: Number(editData.quantity),
        expiryDate: editData.expiryDate,
        batchNumber: editData.batchNumber,
        location: editData.location,
        notes: editData.notes
      };

      // Only include agent if location is 'agent'
      if (editData.location === 'agent' && editData.agent) {
        payload.agent = editData.agent;
      }

      await axios.put(`/api/stock/${editData.id}`, payload);
      closeEditModal();
      fetchStock();
      fetchSummary();
    } catch (editErr) {
      console.error('Error updating stock:', editErr);
      setEditError(editErr.response?.data?.message || 'Failed to update stock.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (stockId) => {
    if (!window.confirm('Delete this stock record?')) return;
    try {
      await axios.delete(`/api/stock/${stockId}`);
      fetchStock();
      fetchSummary();
    } catch (deleteErr) {
      console.error('Error deleting stock:', deleteErr);
    }
  };

  const handleTransferChange = (event) => {
    const { name, value } = event.target;
    setTransferData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTransferItemChange = (index, field, value) => {
    setTransferItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;
        const next = { ...item, [field]: value };
        if (field === 'stockId') {
          const stockItem = allDepotStock.find((row) => row._id === value) || stock.find((row) => row._id === value);
          next.unitPrice = stockItem?.product?.unitPrice ?? '';
        }
        return next;
      })
    );
  };

  const addTransferItem = () => {
    setTransferItems((prev) => [...prev, { stockId: '', quantity: '', unitPrice: '' }]);
  };

  const removeTransferItem = (index) => {
    setTransferItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const depotStockOptions = (() => {
    const stockList = allDepotStock.length > 0 ? allDepotStock : stock.filter((item) => item.location === 'depot');
    
    // Remove duplicates by _id
    const uniqueStock = [];
    const seenIds = new Set();
    stockList.forEach((item) => {
      if (!seenIds.has(item._id)) {
        seenIds.add(item._id);
        uniqueStock.push(item);
      }
    });
    return uniqueStock;
  })();

  const buildTransferItemsPayload = () => {
    return transferItems
      .map((item) => {
        const stockItem = allDepotStock.find((row) => row._id === item.stockId) || stock.find((row) => row._id === item.stockId);
        if (!stockItem) return null;
        return {
          product: stockItem.product?._id,
          quantity: Number(item.quantity),
          expiryDate: stockItem.expiryDate,
          batchNumber: stockItem.batchNumber,
          unitPrice: item.unitPrice === '' || item.unitPrice === null
            ? stockItem.product?.unitPrice
            : Number(item.unitPrice)
        };
      })
      .filter(Boolean);
  };

  const transferTotal = transferItems.reduce((sum, item) => {
    const stockItem = stock.find((row) => row._id === item.stockId);
    const unitPrice = item.unitPrice !== '' && item.unitPrice !== null
      ? Number(item.unitPrice)
      : (stockItem?.product?.unitPrice || 0);
    const qty = Number(item.quantity) || 0;
    return sum + unitPrice * qty;
  }, 0);

  const printTransferReceipt = (transactions, agentInfo) => {
    if (!transactions?.length) return;
    const metaLines = [
      `Receipt ID: ${transactions[0].receiptGroup || transactions[0]._id}`,
      `Date: ${new Date(transactions[0].createdAt).toLocaleString()}`,
      `Agent: ${agentInfo?.name || ''} ${agentInfo?.email ? `(${agentInfo.email})` : ''}`
    ];
    const columns = ['Product', 'Quantity', 'Unit Price', 'Total Value'];
    const rows = transactions.map((transaction) => [
      `${transaction.product?.name || ''}${transaction.product?.size ? ` (${transaction.product.size})` : ''}`,
      transaction.quantity,
      formatCurrency(transaction.unitPrice),
      formatCurrency(transaction.amount)
    ]);
    const totalAmount = transactions.reduce((sum, item) => sum + (item.amount || 0), 0);
    rows.push(['Total', '', '', formatCurrency(totalAmount)]);
    printReceiptWindow({
      title: 'Stock Transfer Receipt',
      metaLines,
      columns,
      rows
    });
  };

  const downloadTransferReceipt = (transactions, agentInfo) => {
    if (!transactions?.length) return;
    const metaLines = [
      `Receipt ID: ${transactions[0].receiptGroup || transactions[0]._id}`,
      `Date: ${new Date(transactions[0].createdAt).toLocaleString()}`,
      `Agent: ${agentInfo?.name || ''} ${agentInfo?.email ? `(${agentInfo.email})` : ''}`
    ];
    const columns = ['Product', 'Quantity', 'Unit Price', 'Total Value'];
    const rows = transactions.map((transaction) => [
      `${transaction.product?.name || ''}${transaction.product?.size ? ` (${transaction.product.size})` : ''}`,
      transaction.quantity,
      formatCurrency(transaction.unitPrice),
      formatCurrency(transaction.amount)
    ]);
    const totalAmount = transactions.reduce((sum, item) => sum + (item.amount || 0), 0);
    rows.push(['Total', '', '', formatCurrency(totalAmount)]);
    downloadReceiptPdf({
      title: 'Stock Transfer Receipt',
      metaLines,
      columns,
      rows,
      filename: `stock-transfer-${transactions[0].receiptGroup || transactions[0]._id}.pdf`
    });
  };

  const handleTransferSubmit = async (event) => {
    event.preventDefault();
    setTransferSubmitting(true);
    setTransferError('');

    try {
      const itemsPayload = buildTransferItemsPayload();
      if (itemsPayload.length === 0) {
        setTransferError('Please add at least one item.');
        setTransferSubmitting(false);
        return;
      }

      const response = await axios.post('/api/stock/transfer', {
        agent: transferData.agent,
        notes: transferData.notes,
        items: itemsPayload
      }, {
        timeout: 30000
      });

      if (response.status === 200 || response.status === 201) {
        const agentInfo = agents.find((agent) => agent._id === transferData.agent);
        setTransferReceipt({
          transactions: response.data.transactions,
          agentInfo
        });
        fetchStock();
        fetchSummary();
      }
    } catch (submitError) {
      console.error('Error transferring stock:', submitError);
      if (submitError.code === 'ECONNABORTED') {
        setTransferError('Request timeout. Please check if the distribution was completed before trying again.');
      } else {
        setTransferError(
          submitError.response?.data?.message || 'Failed to transfer stock. Please check your connection and try again.'
        );
      }
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleMergeDuplicates = async () => {
    if (!window.confirm('This will merge duplicate stock entries with the same product, batch, expiry date, and location. Continue?')) {
      return;
    }

    setMergingDuplicates(true);
    try {
      const response = await axios.post('/api/stock/merge-duplicates');
      alert(`Success! Merged ${response.data.groupsMerged} duplicate groups (removed ${response.data.entriesRemoved} duplicate entries)`);
      fetchStock();
      fetchSummary();
    } catch (error) {
      console.error('Error merging duplicates:', error);
      alert(error.response?.data?.message || 'Failed to merge duplicates');
    } finally {
      setMergingDuplicates(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Stock Management</h1>
          <p className="mt-1 text-sm text-secondary-600">
            {isFieldAgent
              ? 'Today’s received stock is shown by default. Use the date range to look back, or All dates for everything on hand.'
              : 'Monitor inventory levels and track stock movements.'}
          </p>
        </div>
        {(user?.role === 'admin' || user?.role === 'finance') && (
          <div className="flex gap-3">
            {user?.role === 'admin' && (
              <button 
                className="btn-secondary flex items-center gap-2" 
                onClick={handleMergeDuplicates}
                disabled={mergingDuplicates}
              >
                {mergingDuplicates ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Merging...
                  </>
                ) : (
                  'Merge Duplicates'
                )}
              </button>
            )}
            <button className="btn-primary flex items-center gap-2" onClick={openModal}>
              <PlusIcon className="h-5 w-5" />
              Add Stock
            </button>
          </div>
        )}
      </div>

      <div className="filter-section">
        <h3 className="text-base font-bold text-secondary-900 mb-3">Filter Stock Records</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Search stock..."
            className="input-field md:col-span-2"
          />
          <select
            name="location"
            value={filters.location}
            onChange={handleFilterChange}
            className="input-field"
          >
            <option value="">All locations</option>
            <option value="depot">Depot</option>
            <option value="agent">Agent</option>
          </select>
          <select
            name="product"
            value={filters.product}
            onChange={handleFilterChange}
            className="input-field"
          >
            <option value="">All products</option>
            {products.map((product) => (
              <option key={product._id} value={product._id}>
                {product.name} {product.size ? `(${product.size})` : ''}
              </option>
            ))}
          </select>
          {(user?.role === 'admin' || user?.role === 'finance') && (
            <select
              name="agent"
              value={filters.agent}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All agents</option>
              {agents.map((agent) => (
                <option key={agent._id} value={agent._id}>
                  {agent.name} ({agent.email})
                </option>
              ))}
            </select>
          )}
          <div>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="input-field"
              placeholder="From date"
            />
            <label className="text-xs text-secondary-500 mt-1 block">
              {filters.location === 'agent' || filters.agent ? 'Distributed from' : 'Added from'}
            </label>
          </div>
          <div>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="input-field"
              placeholder="To date"
            />
            <label className="text-xs text-secondary-500 mt-1 block">
              {filters.location === 'agent' || filters.agent ? 'Distributed until' : 'Added until'}
            </label>
          </div>
          {isFieldAgent && (
            <div className="flex items-end gap-2">
              <button
                type="button"
                className="btn-secondary h-10"
                onClick={() => {
                  const today = kenyaToday();
                  setFilters((prev) => ({ ...prev, startDate: today, endDate: today }));
                  setPage(1);
                }}
              >
                Today
              </button>
              <button
                type="button"
                className="btn-secondary h-10"
                onClick={() => {
                  setFilters((prev) => ({ ...prev, startDate: '', endDate: '' }));
                  setPage(1);
                }}
              >
                All dates
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stock Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-secondary-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-secondary-600 uppercase">Total Items</p>
              <p className="text-2xl font-bold text-secondary-900 mt-1">{summary.totalItems}</p>
            </div>
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white border border-secondary-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-secondary-600 uppercase">Total Value</p>
              <p className="text-2xl font-bold text-secondary-900 mt-1">{formatCurrency(summary.totalValue)}</p>
            </div>
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Expiring Stock Alert */}
      {!expiringLoading && expiringStock.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Stock Expiring Soon
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  You have {expiringStock.length} item{expiringStock.length === 1 ? '' : 's'} expiring within the next 7 days.
                </p>
                <div className="mt-2 space-y-1 text-sm text-yellow-700">
                  {expiringStock.slice(0, 5).map((item) => (
                    <div key={item._id} className="flex items-center justify-between">
                      <span>
                        {item.product?.name} {item.product?.size ? `(${item.product.size})` : ''}
                      </span>
                      <span className="text-xs text-yellow-800">
                        Exp: {new Date(item.expiryDate).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                  {expiringStock.length > 5 && (
                    <div className="text-xs text-yellow-800">
                      +{expiringStock.length - 5} more expiring items
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Table */}
      <div className="card bg-secondary-50">
        {stock.length === 0 ? (
          <div className="card-body">
            <p className="text-sm text-secondary-500">No stock records yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 text-xs">
              <thead className="bg-secondary-100">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium text-secondary-600 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-2 py-1.5 text-left font-medium text-secondary-600 uppercase tracking-wider">
                    Qty
                  </th>
                  <th className="px-2 py-1.5 text-left font-medium text-secondary-600 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-2 py-1.5 text-left font-medium text-secondary-600 uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-2 py-1.5 text-left font-medium text-secondary-600 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-2 py-1.5 text-left font-medium text-secondary-600 uppercase tracking-wider">
                    Expiry
                  </th>
                  {(user?.role === 'admin' || user?.role === 'finance') && (
                    <th className="px-2 py-1.5 text-left font-medium text-secondary-600 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-100">
                {stock.map((item) => (
                  <tr key={item._id}>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <span className="font-medium text-secondary-900">
                        {item.product?.name}
                      </span>
                      {item.product?.size ? (
                        <span className="text-secondary-500"> {item.product.size}</span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap text-secondary-900">
                      {item.quantity}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap">
                      <span className={`inline-flex px-1.5 py-0.5 font-semibold rounded ${
                        item.location === 'depot'
                          ? 'bg-primary-100 text-primary-800'
                          : 'bg-success-100 text-success-800'
                      }`}>
                        {item.location}
                      </span>
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap text-secondary-900">
                      {item.location === 'agent' ? (item.agent?.name || '—') : '—'}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap text-secondary-900">
                      {new Date(item.location === 'agent' && item.distributedAt ? item.distributedAt : item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-2 py-1 whitespace-nowrap text-secondary-900">
                      {new Date(item.expiryDate).toLocaleDateString()}
                    </td>
                    {(user?.role === 'admin' || user?.role === 'finance') && (
                      <td className="px-2 py-1 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {item.location === 'depot' ? (
                            <button
                              className="text-primary-600 hover:text-primary-700 font-medium"
                              onClick={() => openTransferModal(item)}
                            >
                              Distribute
                            </button>
                          ) : (
                            <span className="text-secondary-400">—</span>
                          )}
                          {user?.role === 'admin' && (
                            <>
                              <button
                                className="text-secondary-600 hover:text-secondary-800 font-medium"
                                onClick={() => openEditModal(item)}
                              >
                                Edit
                              </button>
                              <button
                                className="text-red-600 hover:text-red-700 font-medium"
                                onClick={() => handleDelete(item._id)}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex flex-col items-center justify-between gap-3 border-t border-secondary-100 bg-white px-6 py-4 text-sm text-secondary-600 md:flex-row">
          <div>
            Showing {stock.length} of {total} items
          </div>
          <div className="flex items-center gap-2">
            <select
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPage(1);
              }}
              className="input-field h-9"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={1000}>All</option>
            </select>
            <button
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <button
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-5xl w-full top-10">
            <div className="mt-3">
              <h3 className="text-2xl font-bold text-secondary-900 mb-5 bg-gradient-to-r from-primary-700 to-primary-600 bg-clip-text text-transparent">Add Stock</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Items Section */}
                <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4 border-2 border-primary-200">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-base font-bold text-secondary-900">Stock Items</label>
                    <button type="button" className="btn-secondary text-sm py-2 px-4 flex items-center gap-2" onClick={addStockItem}>
                      <PlusIcon className="h-4 w-4" />
                      Add item
                    </button>
                  </div>
                  <div className="space-y-3">
                    {stockItems.map((item, index) => (
                      <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-secondary-700 mb-1">Product *</label>
                            <select
                              value={item.product}
                              onChange={(e) => handleStockItemChange(index, 'product', e.target.value)}
                              className="input-field"
                              required
                            >
                              <option value="">Select product</option>
                              {products.map((product) => (
                                <option key={product._id} value={product._id}>
                                  {product.name} ({product.size})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-secondary-700 mb-1">Quantity *</label>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleStockItemChange(index, 'quantity', e.target.value)}
                              className="input-field"
                              min="1"
                              placeholder="Qty"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-secondary-700 mb-1">Damaged Qty</label>
                            <input
                              type="number"
                              value={item.damagedQuantity}
                              onChange={(e) => handleStockItemChange(index, 'damagedQuantity', e.target.value)}
                              className="input-field"
                              min="0"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-secondary-700 mb-1">Expiry Date *</label>
                            <input
                              type="date"
                              value={item.expiryDate}
                              onChange={(e) => handleStockItemChange(index, 'expiryDate', e.target.value)}
                              className="input-field"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-secondary-700 mb-1">Batch #</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={item.batchNumber}
                                onChange={(e) => handleStockItemChange(index, 'batchNumber', e.target.value)}
                                className="input-field"
                                placeholder="Auto-generated"
                              />
                              {stockItems.length > 1 && (
                                <button
                                  type="button"
                                  className="text-red-600 hover:text-red-700 font-medium whitespace-nowrap"
                                  onClick={() => removeStockItem(index)}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Location and Agent Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-secondary-700 mb-2">Location *</label>
                    <select
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="input-field"
                      required
                    >
                      <option value="depot">Depot</option>
                      <option value="agent">Agent</option>
                    </select>
                  </div>

                  {formData.location === 'agent' && (
                    <div>
                      <label className="block text-sm font-bold text-secondary-700 mb-2">Agent *</label>
                      <select
                        name="agent"
                        value={formData.agent}
                        onChange={handleChange}
                        className="input-field"
                        required
                      >
                        <option value="">Select agent</option>
                        {agents.map((agent) => (
                          <option key={agent._id} value={agent._id}>
                            {agent.name} ({agent.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Notes Section */}
                <div>
                  <label className="block text-sm font-bold text-secondary-700 mb-2">Notes</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    className="input-field"
                    rows="3"
                    placeholder="Optional notes..."
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 p-4 shadow-sm">
                    <div className="text-sm font-semibold text-red-700">{error}</div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t-2 border-gray-200">
                  <button type="button" className="btn-secondary" onClick={closeModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : `Save ${stockItems.length} Item(s)`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {transferModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content max-w-4xl w-full top-20">
            <div className="mt-3">
              <h3 className="text-2xl font-bold text-secondary-900 mb-5 bg-gradient-to-r from-primary-700 to-primary-600 bg-clip-text text-transparent">Distribute Stock</h3>
              <form onSubmit={handleTransferSubmit} className="space-y-5">
                <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4 border-2 border-primary-200">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-base font-bold text-secondary-900">Distribution Items</label>
                    <button type="button" className="btn-secondary text-sm py-2 px-4 flex items-center gap-2" onClick={addTransferItem}>
                      <PlusIcon className="h-4 w-4" />
                      Add item
                    </button>
                  </div>
                  <div className="space-y-3">
                    {transferItems.map((item, index) => {
                      const stockItem = allDepotStock.find((row) => row._id === item.stockId) || stock.find((row) => row._id === item.stockId);
                      const availableQty = stockItem?.quantity || 0;
                      return (
                        <div key={index} className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_1fr_auto] bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                          <select
                            value={item.stockId}
                            onChange={(event) => handleTransferItemChange(index, 'stockId', event.target.value)}
                            className="input-field"
                            required
                          >
                            <option value="">Select product</option>
                            {depotStockOptions.map((option) => (
                              <option key={option._id} value={option._id}>
                                {option.product?.name}{option.product?.size ? ` (${option.product.size})` : ''} - Exp {new Date(option.expiryDate).toLocaleDateString()} (Avail {option.quantity})
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            value={item.unitPrice}
                            onChange={(event) => handleTransferItemChange(index, 'unitPrice', event.target.value)}
                            className="input-field"
                            min="0"
                            step="0.01"
                            placeholder="Unit price"
                            required
                          />
                          <input
                            type="number"
                            value={item.quantity}
                            onChange={(event) => handleTransferItemChange(index, 'quantity', event.target.value)}
                            className="input-field"
                            min="1"
                            max={availableQty}
                            placeholder="Qty"
                            required
                          />
                          <div className="flex flex-col gap-2">
                            <span className="text-sm font-medium text-secondary-600">
                              Avail: {availableQty}
                            </span>
                            {transferItems.length > 1 && (
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-700 text-sm font-medium"
                                onClick={() => removeTransferItem(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-secondary-700 mb-2">Agent *</label>
                    <select
                      name="agent"
                      value={transferData.agent}
                      onChange={handleTransferChange}
                      className="input-field"
                      required
                    >
                      <option value="">Select agent</option>
                      {agents.map((agent) => (
                        <option key={agent._id} value={agent._id}>
                          {agent.name} ({agent.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-secondary-700 mb-2">Notes</label>
                    <textarea
                      name="notes"
                      value={transferData.notes}
                      onChange={handleTransferChange}
                      className="input-field"
                      rows="3"
                      placeholder="Optional notes..."
                    />
                  </div>
                </div>

                <div className="bg-gradient-to-r from-success-50 to-success-100 border-2 border-success-300 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-base font-bold text-success-900">Grand Total:</span>
                    <span className="text-2xl font-bold text-success-700">{formatCurrency(transferTotal)}</span>
                  </div>
                </div>

                {transferError && (
                  <div className="rounded-lg bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 p-4 shadow-sm">
                    <div className="text-sm font-semibold text-red-700">{transferError}</div>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button type="button" className="btn-secondary" onClick={closeTransferModal} disabled={transferSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={transferSubmitting}>
                    {transferSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </span>
                    ) : 'Distribute'}
                  </button>
                </div>
              </form>
              {transferReceipt && (
                <div className="mt-4 rounded-md border border-secondary-200 bg-secondary-50 p-3">
                  <div className="text-sm font-medium text-secondary-900">Receipt ready</div>
                  <div className="mt-2 flex gap-3">
                    <button
                      className="btn-secondary"
                      onClick={() => printTransferReceipt(transferReceipt.transactions, transferReceipt.agentInfo)}
                    >
                      Print
                    </button>
                    <button
                      className="btn-primary"
                      onClick={() => downloadTransferReceipt(transferReceipt.transactions, transferReceipt.agentInfo)}
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-200 w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">Edit Stock</h3>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Product</label>
                  <input
                    type="text"
                    value={editData.product ? `${editData.product.name} (${editData.product.size})` : ''}
                    className="input-field bg-secondary-100 cursor-not-allowed"
                    disabled
                    readOnly
                  />
                  <p className="mt-1 text-xs text-secondary-500">Product cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700">Quantity</label>
                  <input
                    type="number"
                    name="quantity"
                    value={editData.quantity}
                    onChange={handleEditChange}
                    className="input-field"
                    min="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700">Batch Number</label>
                  <input
                    type="text"
                    name="batchNumber"
                    value={editData.batchNumber}
                    onChange={handleEditChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700">Expiry Date</label>
                  <input
                    type="date"
                    name="expiryDate"
                    value={editData.expiryDate}
                    onChange={handleEditChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700">Location</label>
                  <select
                    name="location"
                    value={editData.location}
                    onChange={handleEditChange}
                    className="input-field"
                    required
                  >
                    <option value="depot">Depot</option>
                    <option value="agent">Agent</option>
                  </select>
                </div>

                {editData.location === 'agent' && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Agent</label>
                    <select
                      name="agent"
                      value={editData.agent}
                      onChange={handleEditChange}
                      className="input-field"
                      required
                    >
                      <option value="">Select agent</option>
                      {agents.map((agent) => (
                        <option key={agent._id} value={agent._id}>
                          {agent.name} ({agent.email})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    name="notes"
                    value={editData.notes}
                    onChange={handleEditChange}
                    className="input-field"
                    rows="3"
                    placeholder="Optional notes about this stock"
                  />
                </div>

                {editError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {editError}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button type="button" className="btn-secondary" onClick={closeEditModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={editSubmitting}>
                    {editSubmitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stock;
